import React, { useState } from 'react';

import { Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AppLoading } from 'src/AppLoading';
import { handleLogout } from 'src/auth';
import { AlineAvatar, AlineButton, AlineTextarea } from 'src/components';
import { AlineLogo } from 'src/components/icons/AlineLogo';
import { RequestDocAccessImg } from 'src/components/icons/RequestDocAccess';
import { homePageRoute } from 'src/config';
import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import type { ReactSetStateAction } from 'src/types';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  card: {
    background: theme.palette.common.white,
    borderRadius: 20,
    boxShadow: '0px 12px 30px 0px rgba(50, 74, 82, 0.15)',
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: '1fr 176px',
    padding: theme.spacing(5),
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    width: 324,
  },
  currentUser: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2, 5),
  },
  gridItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    justifyContent: 'center',
  },
  infoBox: {
    padding: theme.spacing(2, 0),
  },
  requestAccessImg: {
    left: 44,
    position: 'relative',
  },
  root: {
    alignItems: 'center',
    backgroundColor: theme.appColors.background,
    boxSizing: 'border-box',
    display: 'flex',
    height: '100vh',
    justifyContent: 'center',
    width: '100%',
  },
  userDetails: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(2),
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

export function DeactivatedUserPage() {
  const { classes } = useStyles();
  const currentUser = useSelectCurrentUser();
  const [loggingOut, setLoggingOut] = useState<boolean>(false);

  if (!currentUser) return <AppLoading waitingText={'Getting user info...'} />;

  return (
    <div className={classes.root}>
      <div className={classes.wrapper}>
        <div className={classes.card}>
          <div className={classes.gridItem}>
            <AlineLogo />
            <DeactivatedUserPageBody />
          </div>
          <div className={classes.gridItem}>
            <RequestDocAccessImg className={classes.requestAccessImg} />
          </div>
        </div>
        <div className={classes.currentUser}>
          <Typography>You are logged in as:</Typography>
          <div className={classes.userDetails}>
            <AlineAvatar avatarInfo={currentUser} />
            <Typography>{currentUser.displayName}</Typography>
            <AlineButton
              color="primary"
              loading={loggingOut}
              variant="outlined"
              onClick={() => {
                setLoggingOut(true);
                handleLogout();
                setLoggingOut(false);
              }}
            >
              Logout
            </AlineButton>
          </div>
        </div>
      </div>
    </div>
  );
}

interface RequestReactivationProps {
  setRequestSent: ReactSetStateAction<boolean>;
}

function DeactivatedUserPageBody(): JSX.Element {
  const [requestSent, setRequestSent] = useState<boolean>(false);

  if (requestSent) return <RequestSent />;

  return <RequestReactivation setRequestSent={setRequestSent} />;
}

function RequestReactivation({ setRequestSent }: RequestReactivationProps) {
  const { classes } = useStyles();
  const [message, setMessage] = useState<string | undefined>();
  const [requestReactivation, { loading: requestingAccess }] =
    Apollo.useRequestAccountActivationMutation();

  return (
    <div className={classes.container}>
      <Typography variant="h1">This account has been deactivated</Typography>
      <Typography>You can request reactivation from your organization admin below.</Typography>
      <AlineTextarea
        maxRows={5}
        minRows={5}
        placeholder="Add a message for your organization admin(s)..."
        value={message}
        onChange={e => setMessage(e.target.value)}
      />
      <AlineButton
        color="primary"
        loading={requestingAccess}
        variant="contained"
        onClick={async () => {
          const { data } = await requestReactivation({
            variables: { msg: message },
          });
          if (data?.requestAccountActivation) setRequestSent(true);
        }}
      >
        Request Reactivation
      </AlineButton>
    </div>
  );
}

function RequestSent() {
  const { classes } = useStyles();
  const navigate = useNavigate();

  return (
    <div className={classes.container}>
      <div className={classes.infoBox}>
        <Typography variant="h1">Request sent</Typography>
        <Typography>
          {`Your organization admin(s) has received your request and will respond soon.`}
        </Typography>
      </div>
      <AlineButton color="primary" variant="outlined" onClick={() => navigate(homePageRoute)}>
        Go to Aline
      </AlineButton>
    </div>
  );
}
